import { TourCvAnalysis, TourHiringAssistantGif, TourManageJobsGif } from 'assets';

export type ArrowPlacement =
  | 'top-left'
  | 'top-center'
  | 'top-right'
  | 'bottom-left'
  | 'bottom-center'
  | 'bottom-right'
  | 'left-top'
  | 'right-top'
  | 'none';

export type ButtonConfig = 'skip-next' | 'okay' | 'done';

export interface TourStepConfig {
  id: number;
  type: 'modal' | 'tooltip';
  title: string;
  description: string;
  routePattern: string;
  targetId?: string;
  arrowPlacement: ArrowPlacement;
  buttons: ButtonConfig;
  gif?: string;
  media?: string;
  tooltipWidth?: number;
  autoScrollIntoView?: boolean;
  navigateTo?: string; // navigate here when Next/Done is clicked
  offsetX?: number;     // nudge the computed left position by this many pixels
  offsetY?: number;     // nudge the computed top position by this many pixels
  arrowOffsetX?: number; // override the arrow's left/right CSS position in px
  nextLabel?: string;    // override the Next button label
}

export const TOUR_STEPS: TourStepConfig[] = [
  // ── PAGE 1: Hiring Assistant (/employer/assessment) ──────────────────────
  {
    id: 0,
    type: 'tooltip',
    title: 'Hiring Assistant',
    description: 'Start here to analyze CVs or create an assessment for your role.',
    routePattern: '^/employer/assessment',
    targetId: 'tour-nav-hiring-assistant',
    arrowPlacement: 'left-top',
    buttons: 'skip-next',
    gif: TourHiringAssistantGif,
    autoScrollIntoView: true,
    tooltipWidth: 420,
  },
  {
    id: 1,
    type: 'tooltip',
    title: 'CV Analysis',
    description: 'Upload candidate CVs to automatically compare skills and shortlist faster.',
    routePattern: '^/employer/assessment',
    targetId: 'tour-cv-analysis-option',
    arrowPlacement: 'bottom-left',
    buttons: 'skip-next',
    media: TourCvAnalysis,
    autoScrollIntoView: true,
    tooltipWidth: 400,
  },
  {
    id: 2,
    type: 'tooltip',
    title: 'Assessment',
    description: 'Build an assessment and invite candidates to start evaluating immediately.',
    routePattern: '^/employer/assessment',
    targetId: 'tour-hire-option',
    arrowPlacement: 'bottom-right',
    buttons: 'skip-next',
    tooltipWidth: 380,
    gif: TourHiringAssistantGif,
    nextLabel: 'Get Started'
  },

  // ── PAGE 2: Manage Jobs (/employer/manage-jobs) ───────────────────────────
  {
    id: 3,
    type: 'tooltip',
    title: 'Manage Job',
    description: 'Track all assessments and monitor candidate progress from here.',
    routePattern: '^/employer/manage-jobs$',
    targetId: 'tour-nav-manage-jobs',
    arrowPlacement: 'left-top',
    buttons: 'skip-next',
    tooltipWidth: 420,
    gif: TourManageJobsGif
  },
  {
    id: 4,
    type: 'tooltip',
    title: 'All Assessments',
    description: 'View and manage all assessments for your roles here.',
    routePattern: '^/employer/manage-jobs$',
    targetId: 'tour-tab-all',
    arrowPlacement: 'top-left',
    buttons: 'okay',
    autoScrollIntoView: true,
    tooltipWidth: 360,
  },
  {
    id: 5,
    type: 'tooltip',
    title: 'Active',
    description: 'Monitor assessments currently accepting candidates.',
    routePattern: '^/employer/manage-jobs$',
    targetId: 'tour-tab-active',
    arrowPlacement: 'top-center',
    buttons: 'okay',
    autoScrollIntoView: true,
    tooltipWidth: 360,
  },
  {
    id: 6,
    type: 'tooltip',
    title: 'Paused',
    description: 'Resume assessments that are temporarily paused.',
    routePattern: '^/employer/manage-jobs$',
    targetId: 'tour-tab-paused',
    arrowPlacement: 'top-center',
    buttons: 'okay',
    tooltipWidth: 360,
  },
  {
    id: 7,
    type: 'tooltip',
    title: 'Scheduled',
    description: 'See assessments scheduled to open later.',
    routePattern: '^/employer/manage-jobs$',
    targetId: 'tour-tab-scheduled',
    arrowPlacement: 'top-center',
    buttons: 'okay',
    tooltipWidth: 360,
  },
  {
    id: 8,
    type: 'tooltip',
    title: 'Closed',
    description: 'Review completed assessments and candidate results here.',
    routePattern: '^/employer/manage-jobs$',
    targetId: 'tour-tab-closed',
    arrowPlacement: 'top-center',
    buttons: 'okay',
    tooltipWidth: 360,
  },
  {
    id: 9,
    type: 'tooltip',
    title: 'Draft',
    description: 'Access assessments you\'re still preparing to complete them.',
    routePattern: '^/employer/manage-jobs$',
    targetId: 'tour-tab-draft',
    arrowPlacement: 'top-center',
    buttons: 'okay',
    tooltipWidth: 360,
  },

  // ── PAGE 3: Review Talents (/employer/manage-jobs/review-talent/:id) ──────
  {
    id: 10,
    type: 'tooltip',
    title: 'Score Interpretation',
    description: 'Candidate scores show how closely each applicant matches your role requirements.',
    routePattern: '^/employer/manage-jobs/review-talent/[^/]+$',
    targetId: 'tour-score-column',
    arrowPlacement: 'bottom-center',
    buttons: 'skip-next',
    tooltipWidth: 360,
  },
  {
    id: 11,
    type: 'tooltip',
    title: 'Decision Actions',
    description: 'Move candidates forward to Interview, review them later under Probe further, or Reject if they\'re not a fit.',
    routePattern: '^/employer/manage-jobs/review-talent/[^/]+$',
    targetId: 'tour-decision-column',
    arrowPlacement: 'bottom-center',
    buttons: 'skip-next',
    tooltipWidth: 360,
  },
  {
    id: 12,
    type: 'tooltip',
    title: 'Analytics Summary',
    description: 'Track how many candidates are interview-ready and where others need further review.',
    routePattern: '^/employer/manage-jobs/review-talent/[^/]+$',
    targetId: 'tour-analytics-stats',
    arrowPlacement: 'top-right',
    buttons: 'done',
    tooltipWidth: 380,
  },

  // ── PAGE 4: Report (/employer/manage-jobs/review-talent/:id/report/:id2) ──
  {
    id: 13,
    type: 'tooltip',
    title: 'Overall match score',
    description: 'This score shows how closely the candidate aligns with your role requirements across key evaluation areas.',
    routePattern: '^/employer/manage-jobs/review-talent/[^/]+/report/[^/]+',
    targetId: 'tour-report-score',
    arrowPlacement: 'left-top',
    buttons: 'skip-next',
    tooltipWidth: 360,
  },
  {
    id: 14,
    type: 'tooltip',
    title: 'Select your decision',
    description: 'Choose how you\'d like to proceed with this candidate.',
    routePattern: '^/employer/manage-jobs/review-talent/[^/]+/report/[^/]+',
    targetId: 'tour-report-decisions',
    arrowPlacement: 'left-top',
    buttons: 'skip-next',
    tooltipWidth: 360,
  },
  {
    id: 15,
    type: 'tooltip',
    title: 'Alignment',
    description: 'Review how the candidate fits expectations like experience level, work style, domain competence, and workplace qualities.',
    routePattern: '^/employer/manage-jobs/review-talent/[^/]+/report/[^/]+',
    targetId: 'tour-report-alignment',
    arrowPlacement: 'top-left',
    buttons: 'skip-next',
    tooltipWidth: 360,
    arrowOffsetX: 163,
  },
  {
    id: 16,
    type: 'tooltip',
    title: 'Top strengths',
    description: 'These are the candidate\'s strongest attributes based on their assessment results and role alignment.',
    routePattern: '^/employer/manage-jobs/review-talent/[^/]+/report/[^/]+',
    targetId: 'tour-report-strengths',
    arrowPlacement: 'top-center',
    buttons: 'skip-next',
    tooltipWidth: 360,
    autoScrollIntoView: true,
  },
  {
    id: 17,
    type: 'tooltip',
    title: 'Development watchouts',
    description: 'These areas may require support, clarification, or deeper evaluation during interviews.',
    routePattern: '^/employer/manage-jobs/review-talent/[^/]+/report/[^/]+',
    targetId: 'tour-report-watchouts',
    arrowPlacement: 'top-left',
    buttons: 'skip-next',
    tooltipWidth: 360,
    autoScrollIntoView: true,
    arrowOffsetX: 163,
  },
  {
    id: 18,
    type: 'tooltip',
    title: 'Suggested decision',
    description: 'Precision recommends the most suitable next action based on the candidate\'s overall fit.',
    routePattern: '^/employer/manage-jobs/review-talent/[^/]+/report/[^/]+',
    targetId: 'tour-report-suggestion',
    arrowPlacement: 'bottom-left',
    buttons: 'skip-next',
    tooltipWidth: 380,
    arrowOffsetX: 100,
    autoScrollIntoView: true,
  },
  {
    id: 19,
    type: 'tooltip',
    title: 'Make confident comparisons',
    description: 'Use structured comparisons to understand differences in expectations, competencies, and overall role fit.',
    routePattern: '^/employer/manage-jobs/review-talent/[^/]+/report/[^/]+',
    targetId: 'tour-report-compare',
    arrowPlacement: 'top-center',
    buttons: 'done',
    tooltipWidth: 380,
    autoScrollIntoView: true,
  },
];
